/**
 * Versionerade releasenoter. Lägg nyaste överst. `version` används både som
 * visningsetikett och som nyckel i localStorage för auto-open vid version-bump.
 */
export type ReleaseNote = {
  version: string;
  date: string;
  title?: string;
  items: string[];
};

export const RELEASES: ReleaseNote[] = [
  {
    version: "0.25.0",
    date: "2026-04-19",
    title: "Namngivna revisioner",
    items: [
      "Tryck Ctrl+K och välj 'Revisioner' för att öppna snapshot-dialogen — där kan du spara hela ritningen som en namngiven punkt i tiden.",
      "Bra för 'innan kabelomdragning', 'version för granskning' eller liknande — varje revision är en komplett kopia och kan återställas med ett klick.",
      "Revisioner lagras lokalt i browserns IndexedDB (skilda från själva dokumentet). De följer inte med mellan enheter — använd Exportera backup för det.",
      "Återställning ersätter nuvarande ritning med revisionens innehåll. Ångra (Ctrl+Z) funkar direkt efter om det blev fel.",
      "Varje dokument har sin egen revisionslista så du kan köra parallella historiker i samma browser.",
    ],
  },
  {
    version: "0.24.0",
    date: "2026-04-19",
    title: "Flera dokument i samma browser",
    items: [
      "Ny knapp högst upp till vänster visar aktuellt dokumentnamn — klicka för att se alla dokument i denna browser.",
      "Skapa nya dokument via '+ Nytt dokument'; varje dokument får en unik URL (/local/doc-xxxx) så du kan bokmärka det eller dela länken med dig själv.",
      "Dokumentnamnet hämtas från projektmetadata (Inställningar → Projektnamn) och uppdateras automatiskt när du byter namn där.",
      "Varje dokument har sitt eget Yjs/IndexedDB-lager — de delar inga symboler, trådar eller ark med varandra.",
      "Standarddokumentet (/local) går inte att ta bort, men övriga dokument kan plockas bort ur listan via ×-knappen.",
    ],
  },
  {
    version: "0.23.0",
    date: "2026-04-19",
    title: "Duplicera ark",
    items: [
      "Hovra över en ark-tabb och klicka på den nya ⎘-ikonen för att duplicera hela arket — symboler, trådar och kablar följer med.",
      "Alla kloner får nya beteckningar automatiskt så du inte får K1-dubbletter; kablar får nya W-nummer.",
      "Det nya arket heter '{original} (kopia)' och läggs sist i flikraden. Editorn byter direkt till det så du kan börja redigera.",
      "Finns också som 'Duplicera aktuellt ark' i kommandopaletten.",
    ],
  },
  {
    version: "0.22.0",
    date: "2026-04-19",
    title: "Zooma till markering (Ctrl+Shift+F)",
    items: [
      "Tryck Ctrl+Shift+F så zoomar editorn in på de symboler du markerat — perfekt när du vill fokusera på en del av en stor ritning.",
      "Utan markering fungerar genvägen som 'Passa in ritningen' (samma som Ctrl+0).",
      "Kommandot 'Zooma till markering' finns också i kommandopaletten (Ctrl+K).",
    ],
  },
  {
    version: "0.21.0",
    date: "2026-04-19",
    title: "Sparad-indikator i verktygsraden",
    items: [
      "En diskret 'Sparad HH:MM'-text visas längst till höger i verktygsraden — du ser direkt att ändringen är persisterad.",
      "Vid aktiv skrivning visas 'Sparar…' tills IndexedDB-skrivningen är klar (vanligtvis under en halv sekund).",
      "Bekräftar silent autosave från v0.11.0 — inget nytt spara-beteende, bara synlighet på att det fungerar.",
    ],
  },
  {
    version: "0.20.0",
    date: "2026-04-19",
    title: "Kabeltyp i inspektören",
    items: [
      "Ny ruta 'Typ' i tråd-inspektören när du står i en kabel — fyll i tillverkar-beteckningen, t.ex. 'EKK 3G1.5' eller 'FK 5G2.5'.",
      "Typen gäller hela kabeln så alla ledare i W1 delar samma typ — ändrar du en ledare uppdateras alla samtidigt.",
      "Trådlistan (CSV) har ny kolumn 'Kabeltyp' mellan Kabel och Krets, så inköp ser direkt vilken kabel som ska beställas.",
      "Typen visas inte på ritningen (det är metadata), men finns med i backup-filen och i undo-historiken.",
    ],
  },
  {
    version: "0.19.0",
    date: "2026-04-19",
    title: "Pilknapps-nudge av markerade symboler",
    items: [
      "Markera en eller flera symboler och tryck pil-knappar för att flytta dem ett rutsteg (10 px) åt gången — precisionsjustering utan att dra med musen.",
      "Håll Shift inne för fem rutsteg åt gången (50 px) — snabbare flytt för större förflyttningar.",
      "Alla markerade symboler förflyttas i samma undo-steg, så Ctrl+Z rullar tillbaka hela nudgen.",
      "Trådar följer med automatiskt — anslutningspunkterna behåller sin relativa position på symbolerna.",
    ],
  },
  {
    version: "0.18.0",
    date: "2026-04-19",
    title: "Kabelgruppering (W1, W2…)",
    items: [
      "Markera två eller flera trådar och kör 'Gruppera markerade trådar som kabel' i kommandopaletten — du får en kabel med auto-beteckning W1, W2, W3 enligt SS-EN 81346.",
      "Kabelnamnet ritas som [W1] först i trådlabel:en, så alla ledare som ingår i samma kabel syns direkt på ritningen.",
      "Tråd-inspektören har ny sektion 'Kabel' där du kan byta namn (fritext) eller bryta ut en enskild tråd ur kabeln. Tom kabel raderas automatiskt.",
      "CSV-trådlistan har en ny kolumn 'Kabel' så du kan filtrera/sortera per kabel i Excel.",
      "Grund för automatisk kabellista i senare version — samma datamodell används.",
    ],
  },
  {
    version: "0.17.0",
    date: "2026-04-19",
    title: "Tomt-ark-tips och tangentbordsfokus",
    items: [
      "Tomt ark visar nu en diskret påminnelse: dra en symbol från paletten eller tryck Ctrl+K.",
      "Påminnelsen försvinner automatiskt så fort första symbolen är på plats.",
      "Alla knappar i inspector-panelerna och symbolpaletten har nu tydligare tangentbords-fokus (outline-ring) — viktigt för tangentbordsnavigering och WCAG 2.2 AA.",
    ],
  },
  {
    version: "0.16.0",
    date: "2026-04-19",
    title: "Gå till beteckning",
    items: [
      "Öppna kommandopaletten (Ctrl+K) och skriv t.ex. 'K1' eller 'gå till Q2' — editorn zoomar in på rätt symbol, även om den ligger på ett annat ark.",
      "Alla symboler i ritningen listas automatiskt som 'Gå till …'-kommandon.",
      "Stödjer cross-sheet: om K1 ligger på ark 2 och du är på ark 1 byter editorn ark åt dig innan zoom.",
    ],
  },
  {
    version: "0.15.0",
    date: "2026-04-19",
    title: "Öppna backup-fil",
    items: [
      "Kommandot 'Öppna backup-fil (.ep)…' läser in en backup-fil och ersätter din aktuella ritning.",
      "Bekräftelse visas innan importen sker — nuvarande innehåll rensas och ersätts av filens innehåll.",
      "Import loggas som ett undo-steg: Ctrl+Z direkt efter importen rullar tillbaka till föregående ritning.",
      "Bryta backup-fil? Ett felmeddelande visas och ingenting skrivs över.",
    ],
  },
  {
    version: "0.14.0",
    date: "2026-04-19",
    title: "Backup-fil för hela ritningen",
    items: [
      "Kör kommandot 'Ladda ner backup-fil (.ep)' från kommandopaletten så laddas hela ritningen ner som en `.ep`-fil.",
      "Filen innehåller allt: ark, symboler, trådar, inställningar, revision — inget kastas bort.",
      "Lagra på Dropbox, USB eller i e-posten för att gardera mot browser-krasch eller att IndexedDB töms.",
      "Import från backup-fil kommer i en senare version — just nu är detta en 'förvara-för-säkerhets-skull'-knapp.",
    ],
  },
  {
    version: "0.13.0",
    date: "2026-04-19",
    title: "Kategorier i symbolpaletten + zoom-genvägar",
    items: [
      "Symbolpaletten är nu grupperad i Skydd, Kopplingsdon, Apparater, Uttag & anslutning och Mätning & övervakning — lättare att hitta rätt brytare eller kontaktor.",
      "Sök funkar som förut: skriv i rutan så visas en platt träfflista oavsett kategori.",
      "Ctrl+0 passar in hela ritningen, Ctrl++ zoomar in och Ctrl+- zoomar ut.",
    ],
  },
  {
    version: "0.12.0",
    date: "2026-04-19",
    title: "Varningar för tvivelaktiga kopplingar",
    items: [
      "Om du kopplar L1 till L2, eller blandar phase och signal, markeras tråden nu röd med ett litet varningsikon (⚠).",
      "Hovra över varningen för att se orsaken ('Fas-mismatch: L1 mot L2', 'Ovanlig koppling: phase → signal').",
      "Jord-mot-fas blockeras fortsatt redan vid drag — editorn låter dig inte ens skapa den kopplingen.",
      "Varningen är bara en flagga, inte en blockering — du kan fortfarande spara och exportera för att rätta senare.",
    ],
  },
  {
    version: "0.11.0",
    date: "2026-04-19",
    title: "Automatisk spara i browsern",
    items: [
      "Din ritning sparas nu automatiskt lokalt i browserns IndexedDB — stäng fliken och öppna igen, allt finns kvar.",
      "Ingen knapp att trycka på: varje ändring (symbol, tråd, inställning) persisteras direkt efter att undo-fönstret stängts.",
      "När editorn öppnas visas 'Laddar lokal ritning…' tills sparade data är inlästa — vanligtvis några millisekunder.",
      "Data ligger kvar tills du tömmer browserdata för sidan. Moln-sync kommer i nästa fas.",
    ],
  },
  {
    version: "0.10.0",
    date: "2026-04-19",
    title: "Ritningsinställningar",
    items: [
      "Ny knapp 'Inställningar' i verktygsraden — där fyller du i företagsnamn, uppdrag, beställare, konstruktör och revision.",
      "Värdena visas i title blocket när du exporterar PDF; de sparas per ritning och kan ändras när som helst.",
      "Kan också öppnas via kommandopaletten ('Ritningsinställningar').",
      "Ändringarna ingår i undo/redo — ångra-knappen rullar också tillbaka title block-redigeringar.",
    ],
  },
  {
    version: "0.9.0",
    date: "2026-04-19",
    title: "Kretsbeteckning på tråd",
    items: [
      "Ny fritext-ruta 'Krets' i tråd-inspektören — skriv 'F1', 'F1:L1' eller '-F1' och det visas först i trådens label: 'F1:L1 · 1.5 · BN'.",
      "Kretsbeteckningen följer med vid kopiera/klistra och tas med i trådlista-CSV:en i en ny kolumn 'Krets'.",
      "Värdet kan rensas genom att tömma fältet — då återgår tråden till att visa bara area och färg.",
    ],
  },
  {
    version: "0.8.0",
    date: "2026-04-19",
    title: "Justera och fördela symboler",
    items: [
      "Markera flera symboler och tryck Ctrl+Shift+L/R/T/B för att justera dem till vänster/höger/topp/botten.",
      "Fördela tre eller fler symboler jämnt i horisontell eller vertikal riktning — hittas i kommandopaletten.",
      "Centra-justering (vertikal eller horisontell linje) finns också som kommandon.",
      "Alla justeringar snappar till 10px-gridet så anslutningspunkter förblir på linjen.",
    ],
  },
  {
    version: "0.7.0",
    date: "2026-04-19",
    title: "Exportera apparatlista och trådlista",
    items: [
      "Två nya knappar i verktygsraden: Apparatlista och Trådlista — båda laddar ner en CSV som öppnas direkt i Excel/LibreOffice med svenska rubriker och semikolon-separator.",
      "Apparatlistan innehåller beteckning, typ, symbol-id, ark, position och rotation — en rad per symbol, sorterat per ark.",
      "Trådlistan har nummer, area (mm²), IEC-färg, från/till (device:terminal) och ark — en rad per tråd.",
      "Hittas också i kommandopaletten: 'Exportera apparatlista (CSV)' och 'Exportera trådlista (CSV)'.",
    ],
  },
  {
    version: "0.6.0",
    date: "2026-04-19",
    title: "Tråddimension och IEC-färgkoder",
    items: [
      "Markera en tråd för att se en ny panel där du kan välja tvärsnittsarea (1.5/2.5/4/6/10/16 mm²) och IEC-färgkod (BN, BK, GY, BU, GNYE, RD, YE, WH, OG).",
      "Valet projiceras direkt — tråden får rätt färg och label 'K1 · 2.5 · BU' mitt på linjen.",
      "Fritextfält för egna värden: area 0.75 för signalkabel, hex-färg för speciell kabelmärkning.",
      "Undo/redo fungerar som vanligt — värdena lagras som attribut på Conductor så de följer med vid kopia/klistra.",
    ],
  },
  {
    version: "0.5.0",
    date: "2026-04-19",
    title: "Flera ark per ritning",
    items: [
      "En ritning kan nu innehålla flera ark — tabbar nere i editorn visar alla ark i ordning, med aktivt ark markerat.",
      "Tryck på + för nytt ark, dubbelklicka på en tabb för att byta namn, hover:a för att flytta eller radera. Radering av sista arket är spärrat.",
      "Ctrl+PgDn / Ctrl+PgUp bläddrar mellan ark. Kommandon 'Nytt ark', 'Radera ark', 'Nästa/Föregående ark' finns också i kommandopaletten.",
      "Ritnings-nr i title-blocket visar nu 1/N enligt arkens ordning. Radering av ett ark raderar symboler som bara låg där.",
    ],
  },
  {
    version: "0.4.0",
    date: "2026-04-18",
    title: "Anslutningspunkter på korsande trådar",
    items: [
      "När tre eller fler trådar möts på samma terminal ritas en svart fylld punkt — det är nu visuellt entydigt om strömmen kopplas eller bara passerar.",
      "T-anslutningar (en tråd som slutar mitt på en annan) visas också som fylld punkt.",
      "Korsningar utan delad terminal visas fortsatt som hop-båge — oförändrat beteende.",
    ],
  },
  {
    version: "0.3.0",
    date: "2026-04-18",
    title: "Kopiera, klistra in, markera allt",
    items: [
      "Ctrl+C / Ctrl+V kopierar markerade symboler med trådarna mellan dem.",
      "Ctrl+D duplicerar markering direkt (samma sak i ett steg).",
      "Ctrl+A markerar alla symboler på arket; shift-drag eller shift-klick för selektiv markering.",
      "R roterar hela markeringen — fungerar på flera symboler samtidigt.",
      "Nya designationer tilldelas automatiskt vid klistra in (K1 → K4 om K1-K3 redan finns).",
    ],
  },
  {
    version: "0.2.0",
    date: "2026-04-18",
    title: "Rotation och inspector",
    items: [
      "Ny högerpanel Egenskaper: redigera beteckning, rotation och se position för markerad symbol.",
      "Tryck R för att rotera markerad symbol 90°. Finns också i command palette.",
      "Varning när beteckningen redan används på samma ark (SS-EN 81346-dubblett).",
      "Bugfix: rotation nollställdes när man drog en symbol till ny position.",
    ],
  },
  {
    version: "0.1.0",
    date: "2026-04-18",
    title: "Fas 1 — ritkärna",
    items: [
      "Ortogonala trådar bryts visuellt med hop-båge vid korsning utan delad terminal (elektrisk kontinuitet bevarad).",
      "PDF-export i A3 landscape med svensk title block (Ctrl+P eller knappen Exportera PDF).",
      "Ctrl+K öppnar command palette — sök symboler eller kör redigerarkommandon.",
      "Releasenote-dialog: den här rutan öppnas automatiskt vid nästa version.",
    ],
  },
];

export const LATEST_RELEASE = RELEASES[0]!;
export const RELEASE_STORAGE_KEY = "electro-planner:last-seen-release";
